/**
 * WebAddressViewModel - Web platform adapter for AddressViewModel
 * Integrates platform-agnostic AddressViewModel with web platform services
 */

import { useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  createAddressViewModel,
  type AddressViewModel,
  type AddressViewModelDependencies
} from '@restaurant/viewmodels/sheets'
import { createWebPlatformServices, WebAddressService } from '@restaurant/viewmodels'
import { DeliveryService, PlacesService } from '@restaurant/core'
import { apiClient } from '@restaurant/api-client'

/**
 * Web-specific AddressViewModel hook
 * Uses React Router for navigation and browser localStorage for storage
 */
export const useAddressViewModel = (): AddressViewModel => {
  const navigate = useNavigate()
  const location = useLocation()

  // Create web-specific platform services
  const platformServices = createWebPlatformServices(navigate, location)

  // Create service instances (memoized to avoid recreating on each render)
  const deliveryService = useMemo(() => {
    return new DeliveryService({
      apiClient,
    })
  }, [])

  const placesService = useMemo(() => {
    return new PlacesService({
      apiClient,
    })
  }, [])

  // Create web-specific dependencies
  const dependencies: AddressViewModelDependencies = useMemo(() => ({
    addressService: new WebAddressService(deliveryService, placesService),
  }), [deliveryService, placesService])

  // Create and return the platform-agnostic AddressViewModel
  const AddressViewModelHook = createAddressViewModel(platformServices, dependencies)
  return AddressViewModelHook()
}

// Re-export types for convenience
export type { AddressViewModel } from '@restaurant/viewmodels/sheets'